import React, {
    memo,
    Fragment,
    useMemo,
    useCallback,
    useState,
    useRef,
    useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
    createUseStyles,
    Modal,
    Icon,
    Input,
    Advice,
    Loader,
    Table,
    Text,
    Tooltip,
    useTheme,
} from '@Sage/hoi-poi-ui';
import Fuse from 'fuse.js';

import Tour from 'components/Tour';
import { useWeb5Context } from 'lib/Web5Context';
import NoSearchIcon from '../SvgIcons/NoSearchIcon';

import styles from './styles';
const useStyles = createUseStyles(styles, 'ModalOptionsTable');

const options = {
    threshold: 0.2,
    keys: ['descripcion'],
};

const ModalOptionsTable = memo(
    ({
        advice,
        adviceType = 'info',
        className,
        onToggleModal,
        title,
        confirmText,
        cancelText,
        deleteText,
        placeholderSearch,
        isOpen,
        onConfirm,
        onCancel,
        size = 'medium',
        overrides,
        overlayClassName,
        postComponent,
        isLoading,
        inputFocus = true,
        sections,
        columns = [
            { label: 'description', id: 'description' },
            { label: 'value', id: 'value' },
            { label: 'isRelevant', id: 'isRelevant' },
        ],
        onChangeFeatured,
        featuredIds,
        featuredFields,
        onClickInput,
        onCloseTour,
        setFirstRowSelector,
        modalSteps,
        showTour = false,
    }) => {
        const classes = useStyles();
        const theme = useTheme();
        const classNames = classnames(classes.root, className);

        const [search, setSearch] = useState('');
        const firstRowId = useRef(null);
        const [tooltipVisibility, setTooltipVisibility] = useState({});

        const { t } = useWeb5Context();

        const handleToggleModal = useCallback(() => {
            setSearch('');
            firstRowId.current = null;
            onToggleModal && onToggleModal();
        }, [onToggleModal]);

        const handleCancel = useCallback(() => {
            onCancel && onCancel();
            handleToggleModal();
        }, [onCancel, handleToggleModal]);

        const handleConfirm = useCallback(() => {
            onConfirm && onConfirm();
            handleToggleModal();
        }, [handleToggleModal, onConfirm]);

        useLayoutEffect(() => {
            if (showTour && isOpen && firstRowId.current) {
                setTimeout(() => {
                    document
                        .querySelector(`.${firstRowId.current}`)
                        .scrollIntoView({ block: 'center', behavior: 'smooth' });
                }, 1000);
            }
        }, [isOpen, showTour]);

        const handleTooltipVisibility = useCallback((id, visible) => {
            setTooltipVisibility((tooltipVisibility) => ({
                ...tooltipVisibility,
                [id]: visible,
            }));
        }, []);

        const sectionFields = useCallback(
            (rows, key) => {
                const finalRows = rows?.reduce((arr, row) => {
                    if (row.hiddenByDynamism) return arr;

                    if (
                        showTour &&
                        isOpen &&
                        key !== 'featured' &&
                        row.canEditRelevant &&
                        !firstRowId.current
                    ) {
                        firstRowId.current = `description-cell__${row.id}`;
                        setFirstRowSelector(`description-cell__${row.id}`);
                    }
                    const classNamesStarCell = classnames(
                        {
                            [classes.featured]: featuredIds[row.id],
                            [classes.disabled]: !row.canEditRelevant,
                        },
                        `featured-cell__${row.id}`,
                    );

                    const classRow = classnames(classes.row, {
                        [classes.clickableRow]: row.canEditRelevant,
                    });

                    const classStar = classnames({
                        [classes.clickableStar]: row.canEditRelevant,
                    });

                    const tooltip = featuredIds[row.id]
                        ? t('label_popover_unmark_as_featured')
                        : t('label_popover_mark_as_featured');

                    const tooltipId = `${row.id}_${key}`;

                    const iconWrapperProps = {
                        onMouseEnter: () => handleTooltipVisibility(tooltipId, true),
                        onMouseLeave: () => handleTooltipVisibility(tooltipId, false),
                        onClick: () => {
                            handleTooltipVisibility(tooltipId, false);
                            if (!row.canEditRelevant) return;
                            onChangeFeatured(row, !featuredIds[row.id]);
                        },
                    };

                    const finalRow = {
                        ...row,
                        props: {
                            className: classRow,
                        },
                        description: (
                            <Text
                                className={`description-cell__${row.id}`}
                                color={theme.colors.textLight.secondary}
                            >
                                {row.description}
                            </Text>
                        ),
                        isRelevant: (
                            <Tooltip
                                content={t('label_popover_no_editable_featured_field')}
                                visible={!row.canEditRelevant && !!tooltipVisibility[tooltipId]}
                            >
                                <div className={classes.featuredCell}>
                                    {!row.canEditRelevant && (
                                        <div className={classes.lock} {...iconWrapperProps}>
                                            <Icon className={classes.disabled} name="lock" />
                                        </div>
                                    )}
                                    <Tooltip
                                        content={row.canEditRelevant && tooltip}
                                        visible={
                                            row.canEditRelevant && !!tooltipVisibility[tooltipId]
                                        }
                                    >
                                        <div className={classStar} {...iconWrapperProps}>
                                            {featuredIds[row.id] && (
                                                <Icon
                                                    color={theme.colors.semantic.focus500}
                                                    className={classNamesStarCell}
                                                    name="star"
                                                />
                                            )}
                                            {!featuredIds[row.id] && (
                                                <Icon
                                                    color={
                                                        !!tooltipVisibility[tooltipId]
                                                            ? theme.colors.semantic.focus500
                                                            : theme.colors.utility.textSecondary
                                                    }
                                                    className={classNamesStarCell}
                                                    name="starBorder"
                                                />
                                            )}
                                        </div>
                                    </Tooltip>
                                </div>
                            </Tooltip>
                        ),
                    };

                    arr.push(finalRow);

                    return arr;
                }, []);

                return <Table key={key} rows={finalRows} columns={columns} />;
            },
            [
                columns,
                showTour,
                isOpen,
                classes.featured,
                classes.disabled,
                classes.row,
                classes.clickableRow,
                classes.clickableStar,
                classes.featuredCell,
                classes.lock,
                featuredIds,
                t,
                tooltipVisibility,
                setFirstRowSelector,
                handleTooltipVisibility,
                onChangeFeatured,
                theme,
            ],
        );

        const baseSchema = useMemo(() => {
            return sections.reduce((arr, section, key) => {
                if (section.isHidden) return arr;
                const newSection = (
                    <Fragment>
                        <Text bold>{section.description}</Text>
                        <div className={classes.table}>{sectionFields(section.data, key)}</div>
                    </Fragment>
                );
                arr.push(newSection);
                return arr;
            }, []);
        }, [classes.table, sectionFields, sections]);

        const flatRows = useMemo(
            () =>
                sections.reduce((arr, section) => {
                    arr.push(...section.data);
                    return arr;
                }, []),
            [sections],
        );

        const finalSections = useCallback(
            (sections) => {
                const fuse = new Fuse(flatRows, options);
                if (!sections) return;
                if (search) {
                    const matchingOptions = fuse
                        .search(search.toLowerCase())
                        .map((result) => result.item);
                    if (!matchingOptions?.length)
                        return (
                            <div className={classes?.emptySearchContainer}>
                                <div className={classes?.emptySearchIcon}>
                                    <NoSearchIcon color={theme.colors.grey[500]} />
                                </div>
                                <Text type="subtitle" color={theme.colors.grey[500]}>
                                    {t('label_no_results')}
                                </Text>
                            </div>
                        );
                    return sectionFields(matchingOptions);
                } else {
                    return baseSchema;
                }
            },
            [flatRows, search, classes, theme, sectionFields, baseSchema, t],
        );

        const preComponent = useMemo(() => {
            return (
                <Fragment>
                    {advice && (
                        <Advice
                            type={adviceType}
                            className={classes.advice}
                            showIcon
                            showCollapse={false}
                        >
                            {advice}
                        </Advice>
                    )}
                    <Fragment>
                        <Input
                            placeholder={placeholderSearch}
                            onChange={setSearch}
                            value={search}
                            preComponent={<Icon name="search" />}
                            autoFocus={inputFocus}
                            onClick={onClickInput}
                            isFullWidth
                        />
                    </Fragment>
                </Fragment>
            );
        }, [
            advice,
            adviceType,
            classes.advice,
            placeholderSearch,
            search,
            inputFocus,
            onClickInput,
        ]);

        const overridesProps = useMemo(
            () => ({
                content: {
                    style: isLoading
                        ? {
                              height: '100%',
                          }
                        : {
                              height: '100%',
                              borderRadius: '4px',
                          },
                },
                ...overrides,
            }),
            [isLoading, overrides],
        );

        const getFeaturedFields = useMemo(() => {
            const visibleFeaturedFields = featuredFields.filter((field) => !field.hiddenByDynamism);
            const showFeaturedFields = !!visibleFeaturedFields.length && !search;
            return showFeaturedFields ? (
                <Fragment>
                    <Text bold>{t('label_modal_featured_customfields')}</Text>
                    <div className={classes.table}>{sectionFields(featuredFields, 'featured')}</div>
                </Fragment>
            ) : null;
        }, [classes.table, featuredFields, search, sectionFields, t]);

        return (
            <Fragment>
                {showTour && isOpen && (
                    <Tour onClose={onCloseTour} steps={modalSteps} delay={500} />
                )}
                <Modal
                    className={classNames}
                    overlayClassName={overlayClassName || classes.overlay}
                    title={title}
                    isOpen={isOpen}
                    size={size}
                    useContentStaticHeight={true}
                    onRequestClose={handleCancel}
                    onCancel={!isLoading ? handleCancel : undefined}
                    onConfirm={!isLoading ? handleConfirm : undefined}
                    cancelText={cancelText}
                    confirmText={confirmText}
                    deleteText={deleteText}
                    preComponent={!isLoading && preComponent}
                    postComponent={!isLoading && postComponent}
                    overrides={overridesProps}
                >
                    {isLoading && (
                        <div className={classes.loader}>
                            <Loader size="massive" />
                        </div>
                    )}
                    {!isLoading && (
                        <div className={classes.section}>
                            {getFeaturedFields}
                            {finalSections(sections)}
                        </div>
                    )}
                </Modal>
            </Fragment>
        );
    },
);

ModalOptionsTable.propTypes = {
    advice: PropTypes.string,
    adviceType: PropTypes.oneOf(['error', 'info', 'success', 'warning']),
    className: PropTypes.string,
    isOpen: PropTypes.bool,
    onToggleModal: PropTypes.func,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    overrides: PropTypes.object,
    postComponent: PropTypes.node,
    overlayClassName: PropTypes.string,
    isLoading: PropTypes.bool,
};

export default ModalOptionsTable;
